import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { getVendorIds, resolveGqlErrors } from '@/helpers';
import { useIdlePooling } from '@/hooks';
import { EventPreview } from '../types';
import { aggregateDeliveryEventVendors, getEventPreview } from '../helpers';
import { getCompletedEvents } from '@/api/operations/get-completed-events-search';

type UseCompletedSection = () => {
  loading: boolean;
  error?: string;
  events: EventPreview[];
  eventsToShow: EventPreview[];
  moreEvents: () => void;
  showMore: boolean;
  extendForMonth: boolean;
};

const EVENTS_TO_SHOW = 10;

const useCompletedSection: UseCompletedSection = () => {
  const vendorIds = getVendorIds();
  const [extendForMonth, setExtend] = useState(false);
  const [more, setMore] = useState(false);

  const startTimeBefore = useMemo(() => dayjs().endOf('day').format(), []);
  const startTimeAfter = useMemo(
    () =>
      (extendForMonth ? dayjs().subtract(1, 'month') : dayjs().subtract(2, 'day'))
        .startOf('day')
        .format(),
    [extendForMonth]
  );

  const {
    loading,
    data: eventsData,
    error,
    stopPolling,
    startPolling,
  } = useQuery(getCompletedEvents, {
    variables: {
      vendorIds,
      startTimeBefore,
      startTimeAfter,
    },
    skip: vendorIds.length === 0,
  });

  useIdlePooling({ startPolling, stopPolling });

  useEffect(() => {
    if (extendForMonth || !eventsData?.searchEventVendors?.eventVendors?.nodes) {
      return;
    }

    // if there is no events in the last 2 days, extend the range to 1 month
    if (!eventsData.searchEventVendors.eventVendors.nodes.length) {
      setExtend(true);
    }
  }, [eventsData]);

  const completedEvents = getEventPreview(
    aggregateDeliveryEventVendors(
      eventsData?.searchEventVendors?.eventVendors?.nodes?.filter((event) => event !== null)
    ),
    true
  );

  const events = completedEvents.toSorted(
    (a, b) => dayjs(b.eventTime).valueOf() - dayjs(a.eventTime).valueOf()
  );
  const eventsToShow = more ? events : events.slice(0, EVENTS_TO_SHOW);

  return {
    loading,
    error: resolveGqlErrors(error || eventsData?.searchEventVendors?.error),
    events,
    eventsToShow,
    moreEvents: () => setMore(true),
    showMore: events.length > EVENTS_TO_SHOW && !more,
    extendForMonth,
  };
};

export default useCompletedSection;
